import { Inject, Injectable, InjectionToken } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { Filter } from '@shared/ui';
import { SaveFilterConfigUseCase, LoadFilterConfigsUseCase, DeleteFilterConfigUseCase } from '@analysis/use-cases';
import { ProtocolStore } from '@telespot/protocols/ui';
import { AuthService } from '@telespot/web-core';
import { FilterConfigStore } from '../../stores';

export const SAVE_FILTER_CONFIG_USE_CASE = new InjectionToken<SaveFilterConfigUseCase>('SAVE_FILTER_CONFIG_USE_CASE');
export const LOAD_FILTER_CONFIGS_USE_CASE = new InjectionToken<LoadFilterConfigsUseCase>(
  'LOAD_FILTER_CONFIGS_USE_CASE'
);
export const DELETE_FILTER_CONFIG_USE_CASE = new InjectionToken<DeleteFilterConfigUseCase>(
  'DELETE_FILTER_CONFIG_USE_CASE'
);

@Injectable()
export class FilterConfigEffects {
  constructor(
    @Inject(SAVE_FILTER_CONFIG_USE_CASE) private readonly saveFilterConfigUseCase: SaveFilterConfigUseCase,
    @Inject(LOAD_FILTER_CONFIGS_USE_CASE) private readonly loadFilterConfigsUseCase: LoadFilterConfigsUseCase,
    @Inject(DELETE_FILTER_CONFIG_USE_CASE) private readonly deleteFilterConfigUseCase: DeleteFilterConfigUseCase,
    private readonly protocolStore: ProtocolStore,
    private readonly authService: AuthService,
    private readonly store: FilterConfigStore
  ) {}

  public readonly loadConfigs = this.store.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      concatLatestFrom(() => [this.store.loaded$, this.protocolStore.methodTypeId$, this.authService.currentUser$]),
      filter(([, loaded, methodTypeId, currentUser]) => !!methodTypeId && !!currentUser?.id && !loaded),
      tap(() => this.store.setLoading(true)),
      switchMap(([, , methodTypeId, currentUser]) =>
        from(this.loadFilterConfigsUseCase.execute({ protocolId: methodTypeId, userId: currentUser.id })).pipe(
          tap({
            next: (configs) => this.store.setConfigs(configs),
            error: (error) => this.store.setError(error.message || 'Failed to load filter configurations'),
          }),
          catchError(() => EMPTY)
        )
      )
    )
  );

  public readonly saveConfig = this.store.effect((params$: Observable<{ filters: Filter[]; name?: string }>) =>
    params$.pipe(
      concatLatestFrom(() => [this.protocolStore.methodTypeId$, this.authService.currentUser$]),
      filter(([, methodTypeId, currentUser]) => !!methodTypeId && !!currentUser?.id),
      tap(() => this.store.setLoading(true)),
      switchMap(([{ filters, name }, methodTypeId, currentUser]) => {
        // Map the UI Filter[] to UI-agnostic FilterCriterion[] before persisting, so the
        // domain/application layers stay free of @shared/ui (and the non-serializable
        // SearchField callbacks). The SearchField is rebuilt on apply from the live registry.
        const criteria = filters.map((f) => ({ fieldId: f.field.id, operator: f.operator, value: f.value }));
        return from(this.saveFilterConfigUseCase.execute({ protocolId: methodTypeId, userId: currentUser.id, filters: criteria, name })).pipe(
          tap({
            next: (config) => this.store.addConfig(config),
            error: (error) => this.store.setError(error.message || 'Failed to save filter configuration'),
          }),
          catchError(() => of(null))
        );
      })
    )
  );

  public readonly deleteConfig = this.store.effect((configId$: Observable<string>) =>
    configId$.pipe(
      concatLatestFrom(() => [this.protocolStore.methodTypeId$, this.authService.currentUser$]),
      filter(([, methodTypeId, currentUser]) => !!methodTypeId && !!currentUser?.id),
      tap(() => this.store.setLoading(true)),
      switchMap(([configId, methodTypeId, currentUser]) =>
        from(this.deleteFilterConfigUseCase.execute({ protocolId: methodTypeId, userId: currentUser.id, configId })).pipe(
          tap({
            next: () => this.store.removeConfig(configId),
            error: (error) => this.store.setError(error.message || 'Failed to delete filter configuration'),
          }),
          catchError(() => of(null))
        )
      )
    )
  );
}
